<script>
    import { onMount } from "svelte";

    import { isMobile, user } from "../../stores";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        Button,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getText,
        goBack,
        goToURL,
        replaceURL,
        showError,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    export let params = {};

    $: isUserAdmin = $user.role == 100;

    let isGettingGroups = false,
        returnGroupsArr = [],
        filters = {};

    let page = 0,
        pageSize = 25,
        count;

    const columns = [
        {
            key: "groupName",
            title: "Group Name",
            headerClass: "text-left px-3 w-[25%]  rounded-tl-xl",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.name),
        },
        {
            key: "contactName",
            title: "Contact Name",
            headerClass: "text-left px-3 w-[25%] cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.contactName),
        },
        {
            key: "contactEmail",
            title: "Contact Email",
            headerClass: "text-left px-3 w-[25%] cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.contactEmail),
        },
        {
            key: "contactPhone",
            title: "Contact Phone",
            headerClass: "text-left px-3 w-[25%] cursor-default rounded-tr-xl",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.contactPhone),
        },
    ];

    onMount(() => {
        if (!isUserAdmin) goBack();

        if (params.query) updateSearchParamsByQuery(params.query);

        loadGroups(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();
            }
            updateURL();
        }
    };

    const loadGroups = async (start, limit, params) => {
        isGettingGroups = true;

        await apiMethods.getMethod
            .getGroups(start, limit, params)
            .then((res) => _handleGroups(res))
            .catch((err) => showException(err));

        isGettingGroups = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadGroups(0, pageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (rowData.id) goToURL(`/Groups/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadGroups(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadGroups(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/Groups/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    // HELPER FUNCTIONS
    const _handleGroups = (res) => {
        if (res.error)
            showError(
                "Failed to load groups data, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            returnGroupsArr = [...res.results];
            count = res.total;
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _handleCreateGroup = () => goToURL("/Groups/create/new");
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div class="flex flex-row justify-between flex-wrap">
        <div class="flex gap-4 flex-wrap">
            <SearchInput
                bind:value={filters.search}
                title="Search"
                inputFunc={handleFilterSelect}
                isLong
            />
        </div>

        <Button clickFunc={_handleCreateGroup}>Create Group</Button>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingGroups}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-scroll overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnGroupsArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnGroupsArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnGroupsArr,
            fileName: "Groups",
            allDataApi: async () =>
                await apiMethods.getMethod.getGroups(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>